import { useState } from 'react';
import { Input, ChevronRight, Spinner } from '@react-apps/design-system';
import { useLogin } from '../hooks/use-login';
import { useInstitutionStore } from '../store/institution.store';
import { getTurmaLabel } from '@/config/turma-labels';
import logoAristoDark from '@/assets/image/logo-aristo-dark.svg';
import logoAristoLight from '@/assets/image/logo-aristo-light.png';
import logoFagDark from '@/assets/image/logo-fag-dark.png';
import logoFagLight from '@/assets/image/logo-fag-light.png';

function getInstitutionInitials(institution: string): string {
  return institution.trim().slice(0, 2).toUpperCase();
}

export function LoginModal() {
  const {
    login,
    selectOption,
    cancelSelection,
    options,
    isSwitching,
    isLoading,
    error,
  } = useLogin();
  const { institutionName } = useInstitutionStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const isFag = institutionName?.toUpperCase() === 'FAG';
  const hasOptions = !!options;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login({ email, password });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" />

      <div
        className={`border-(--color-border) bg-(--color-bg-primary) relative z-10 w-full rounded-2xl border p-8 shadow-2xl transition-[max-width] duration-200 ${hasOptions ? 'max-w-md' : 'max-w-sm'}`}
      >
        <div className="mb-6 text-center">
          <div className="mb-5 flex items-center justify-center gap-4">
            <img
              src={logoAristoLight}
              alt="Aristo"
              className="h-7 dark:hidden"
            />
            <img
              src={logoAristoDark}
              alt="Aristo"
              className="hidden h-7 dark:block"
            />
            {isFag && (
              <>
                <span className="border-(--color-border) h-6 w-px border-l" />
                <img src={logoFagDark} alt="FAG" className="h-7 dark:hidden" />
                <img
                  src={logoFagLight}
                  alt="FAG"
                  className="hidden h-7 dark:block"
                />
              </>
            )}
          </div>

          <h2 className="text-(--color-text-primary) text-xl font-bold">
            Dashboard Aristo - SINAREM
          </h2>
        </div>

        {options ? (
          <div className="space-y-5">
            <div className="text-center">
              <h3 className="text-(--color-text-primary) text-base font-semibold">
                {options.length > 1 ? 'Selecione a turma' : 'Confirme a turma'}
              </h3>
              <p className="text-(--color-text-secondary) mt-1 text-sm">
                {options.length > 1
                  ? `Seu login tem acesso a ${options.length} turmas. Escolha qual deseja visualizar.`
                  : 'Confirme abaixo qual turma deseja visualizar.'}
              </p>
            </div>

            <div className="space-y-2.5">
              {options.map((option) => (
                <button
                  key={`${option.institution}:${option.courseId}`}
                  type="button"
                  disabled={isLoading}
                  onClick={() => selectOption(option)}
                  className="group border-(--color-border) bg-(--color-bg-secondary) hover:border-(--color-accent) hover:shadow-(--color-accent)/10 relative flex w-full items-center gap-3.5 overflow-hidden rounded-xl border px-4 py-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
                >
                  <span className="bg-(--color-accent) absolute inset-y-0 left-0 w-1 -translate-x-1 transition-transform group-hover:translate-x-0" />

                  <span className="bg-(--color-accent)/15 text-(--color-accent) flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold">
                    {getInstitutionInitials(option.institution)}
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="text-(--color-text-primary) truncate text-sm font-semibold">
                      {getTurmaLabel(option.description)}
                    </p>
                    <span className="bg-(--color-accent)/10 text-(--color-accent) mt-1 inline-block truncate rounded-full px-2 py-0.5 text-[11px] font-medium tracking-wide uppercase">
                      {option.institution}
                    </span>
                  </div>

                  <span className="bg-(--color-bg-primary) text-(--color-text-secondary) group-hover:bg-(--color-accent) flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors group-hover:text-white">
                    <ChevronRight size="xs" />
                  </span>
                </button>
              ))}
            </div>

            {error && (
              <p className="text-center text-sm text-red-500" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={cancelSelection}
              disabled={isLoading}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) w-full text-center text-sm underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSwitching ? 'Cancelar' : 'Voltar'}
            </button>
          </div>
        ) : isSwitching ? (
          <div className="flex flex-col items-center gap-3 py-6">
            <Spinner size="lg" className="text-(--color-accent)" />
            <p className="text-(--color-text-secondary) text-sm">
              Carregando suas turmas...
            </p>
            {error && (
              <p className="text-center text-sm text-red-500" role="alert">
                {error}
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="E-mail"
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
              autoFocus
              autoComplete="email"
            />

            <Input
              label="Senha"
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />

            {error && (
              <p className="text-sm text-red-500" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="bg-(--color-accent) hover:bg-(--color-accent)/90 mt-2 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
